import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { HelmetProvider } from 'react-helmet-async'; // Import HelmetProvider for SEO
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Navbar from './components/Navbar';
import JobHistory from './components/JobHistory';
import FileUpload from './components/FileUpload';
import AccountInformation from './components/AccountInformation';
import LandingPage from './components/LandingPage'; // Import the new LandingPage component
import ConnectionDebug from './components/ConnectionDebug'; // Import the debugging component

// Get Stripe public key with fallback for development
const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY || '';

// Only initialize Stripe if we have a valid key
const stripePromise = stripePublicKey ? 
  loadStripe(stripePublicKey) : 
  Promise.resolve(null) as Promise<null>;

// Log warning if Stripe key is missing
if (!stripePublicKey) {
  console.warn('Stripe public key is missing. Stripe functionality will be limited.');
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  console.log('AppContent rendering, isAuthenticated:', isAuthenticated);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Only show Navbar on authenticated routes */}
      {isAuthenticated && <Navbar />}
      
      <div className={isAuthenticated ? "container mx-auto px-4 py-8" : ""}>
        <Routes>
          {/* Landing page as the default route */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Login route - now separate from landing page */}
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} />
          
          {/* Debug route - accessible without authentication */}
          <Route path="/debug" element={<ConnectionDebug />} />
          
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
          />
          <Route
            path="/jobs"
            element={isAuthenticated ? <JobHistory /> : <Navigate to="/login" />}
          />
          <Route
            path="/upload"
            element={isAuthenticated ? <FileUpload /> : <Navigate to="/login" />}
          />
          <Route
            path="/account"
            element={isAuthenticated ? <AccountInformation /> : <Navigate to="/login" />}
          />
          
          {/* Catch-all route - redirect to landing */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <HelmetProvider> {/* Add HelmetProvider for SEO */}
        <Elements stripe={stripePromise}>
          <Router>
            <AppContent />
          </Router>
        </Elements>
      </HelmetProvider>
    </AuthProvider>
  );
}

export default App;